import React from "react";
import "./legal.scss";

function PrivacyPage() {
  return (
    <div className="legalPage">
      <h1>Speqqle Privacy Statement</h1>
      <p><em>Last updated: 07 October 2025</em></p>
      <p><em>Effective as of 09 October 2025 (replaces the &quot;Privacy Statement for Beta Testers&quot;)</em></p>

      <div className="section">
        <h2>1. Introduction</h2>
        <p>
          Speqqle SA (&quot;Speqqle&quot;, &quot;we&quot;, &quot;us&quot;) is committed to protecting your privacy and
          handling your personal data in accordance with the EU General Data Protection Regulation (GDPR)
          and the Swiss Federal Act on Data Protection (FADP).
        </p>
        <p>
          This Privacy Statement explains what personal data we collect, how we use it, on what legal
          bases, with whom we share it, how long we keep it, and the rights you have. It applies to all
          users of the Speqqle platform—including users on a free <strong>trial</strong> and those on
          <strong> paid</strong> subscription plans.
        </p>
      </div>

      <div className="section">
        <h2>2. Data Controller</h2>
        <p><strong>Speqqle SA</strong><br />
          Route de la Galaise 34<br />
          1228 Plan-les-Ouates, Switzerland<br />
          Email: <a href="mailto:contact@speqqle.com">contact@speqqle.com</a>
        </p>
      </div>

      <div className="section">
        <h2>3. Personal Data We Process</h2>
        <ul>
          <li>
            <strong>Identity &amp; Contact Data:</strong> name, email address, and affiliation/organization.
          </li>
          <li>
            <strong>Account &amp; Subscription Data:</strong> account credentials, plan type (Trial, Basic,
            or other paid tier), subscription status, renewal dates, applied discounts, Stripe customer ID,
            invoice/receipt identifiers.
          </li>
          <li>
            <strong>Billing &amp; Payments (via Stripe):</strong> we do <u>not</u> collect or store your full
            payment card details. Payments are processed by Stripe (PCI-DSS compliant). We receive limited
            billing metadata (e.g., successful/failed charge status, last four digits and card brand if you
            permit Stripe to share, invoice PDFs, refund status) necessary for billing and support.
          </li>
          <li>
            <strong>Usage &amp; Telemetry:</strong> session start/end timestamps, session duration, feature
            usage, workflow steps, performance metrics, and high-level counts (e.g., number of runs). We do
            <u>not</u> use cookies; analytics are based on server-side events and application logs.
          </li>
          <li>
            <strong>Diagnostics &amp; Support:</strong> error messages, stack traces, bug reports, support
            tickets, and relevant technical context you provide (including sample data you <em>choose</em> to
            share with support).
          </li>
          <li>
            <strong>User Data (your work product):</strong> spectral data, metadata, models, and results that
            you upload or generate. <em>We only retain stored projects if your plan includes data storage or if
            you explicitly opt in.</em> See also the Terms of Use. 
          </li>
          <li>
            <strong>Marketing Preferences:</strong> your consent status for receiving marketing communications.
          </li>
        </ul>
      </div>

      <div className="section">
        <h2>4. Purposes &amp; Legal Bases for Processing</h2>
        <ul>
          <li>
            <strong>Provide and operate the platform</strong> (create accounts, authenticate, run analyses,
            maintain subscriptions, enable features). <em>Legal basis:</em> contract performance (GDPR
            Art. 6(1)(b)); legitimate interests for service operation (Art. 6(1)(f)).
          </li>
          <li>
            <strong>Billing and payments</strong> (process charges, issue invoices, handle refunds).
            <em>Legal basis:</em> contract performance (Art. 6(1)(b)); legal obligations (tax/accounting)
            (Art. 6(1)(c)).
          </li>
          <li>
            <strong>Service communications</strong> (renewal reminders, plan changes, security alerts,
            service updates). <em>Legal basis:</em> contract performance (Art. 6(1)(b)); legitimate
            interests (Art. 6(1)(f)).
          </li>
          <li>
            <strong>Analytics, diagnostics, and product improvement</strong> (usage understanding, stability,
            performance, prioritizing features). <em>Legal basis:</em> legitimate interests (Art. 6(1)(f)).
            Where we use fully anonymized data, GDPR no longer applies.
          </li>
          <li>
            <strong>Marketing communications</strong> (news, offers for higher tiers, new features) sent by
            email only with your prior consent. <em>Legal basis:</em> consent (Art. 6(1)(a)). You can withdraw
            consent at any time.
          </li>
          <li>
            <strong>Legal compliance</strong> (tax, accounting, fraud prevention, law enforcement requests).
            <em>Legal basis:</em> legal obligation (Art. 6(1)(c)).
          </li>
        </ul>
      </div>

      <div className="section">
        <h2>5. How We Use Your Data</h2>
        <ul>
          <li>To register and manage your account and subscription (trial and paid).</li>
          <li>To process payments via Stripe and issue invoices/receipts.</li>
          <li>To send essential service communications and renewal reminders.</li>
          <li>To monitor reliability, diagnose issues, and improve user experience.</li>
          <li>To provide support when you contact us (and only use shared files for that purpose).</li>
          <li>To generate anonymized, aggregated insights (never identifying you or your organization).</li>
        </ul>
        <p><strong>No cookies:</strong> we do not use cookies or third-party trackers. Analytics are derived from
        server-side events and application logs.</p>
      </div>

      <div className="section">
        <h2>6. Sharing with Third Parties</h2>
        <p>We do not sell your personal data. We share data only with:</p>
        <ul>
          <li>
            <strong>Payment processor:</strong> Stripe, for secure payment handling. Stripe processes your card
            data; we do not store full card details.
          </li>
          <li>
            <strong>Hosting &amp; infrastructure providers:</strong> to run our platform (compute, storage,
            backups, logging, email delivery).
          </li>
          <li>
            <strong>Support tools:</strong> systems used to manage tickets and bug reports.
          </li>
          <li>
            <strong>Legal/Compliance:</strong> where required by law or to protect rights, safety, and security.
          </li>
        </ul>
        <p>All service providers are bound by contracts to process data only under our instructions and to keep it secure.</p>
      </div>

      <div className="section">
        <h2>7. Data Retention</h2>
        <p>
          We retain personal data only as long as necessary for the purposes described above, subject to
          statutory retention obligations (e.g., tax/accounting). Typical retention schedules:
        </p>
        <ul>
          <li>
            <strong>Account &amp; subscription data:</strong> for the life of your account and up to
            36 months after closure for billing, audit, and fraud-prevention purposes, unless a longer period
            is required by law.
          </li>
          <li>
            <strong>User Data (stored projects):</strong> retained only if your plan includes data storage or
            you explicitly opt in. Upon cancellation/termination, you can export within a 30-day grace period;
            thereafter deletion follows our standard schedules.
          </li>
          <li>
            <strong>Usage logs &amp; diagnostics:</strong> limited retention to support security, reliability,
            and troubleshooting, after which logs are overwritten or deleted.
          </li>
          <li>
            <strong>Backups:</strong> encrypted backups are kept for disaster-recovery in rolling windows
           and then expire automatically.
          </li>
        </ul>
        <p>
          <strong>Technical traces:</strong> temporary copies or fragments of User Data may persist in caches,
          logs, or backups due to normal operations or unexpected interruptions. Such residual data is not
          accessed, analyzed, or shared, and is removed through routine maintenance and backup expiry.
        </p>
      </div>

      <div className="section">
        <h2>8. International Data Transfers</h2>
        <p>
          We aim to store and process data in Switzerland and/or the European Economic Area (EEA). If data is
          transferred outside Switzerland/EEA (e.g., to sub-processors or Stripe affiliates), we rely on
          appropriate safeguards such as adequacy decisions or Standard Contractual Clauses (SCCs) to ensure an
          equivalent level of protection.
        </p>
        <p>
          <strong>International Users:</strong> Speqqle is based in Switzerland and operates globally.
          By using the platform, you acknowledge that your personal data may be transferred to and processed
          in Switzerland and the European Economic Area, where data protection standards meet or exceed those
          of most jurisdictions, including the GDPR, FADP, CCPA/CPRA, PIPL, and similar frameworks. We apply
          equivalent safeguards to all users, regardless of location.
        </p>
        <p>
          <strong>Note for Users in China:</strong> By using the Speqqle platform, you acknowledge that your
          personal data will be transferred to and processed in Switzerland and/or the EEA. We apply
          safeguards that are intended to meet the requirements of China’s Personal Information Protection
          Law (PIPL), including appropriate contractual commitments with our service providers.
        </p>
      </div>

      <div className="section">
        <h2>9. Your Rights</h2>
        <ul>
          <li><strong>Access:</strong> obtain a copy of your personal data.</li>
          <li><strong>Rectification:</strong> correct inaccurate or incomplete data.</li>
          <li><strong>Erasure:</strong> request deletion when data is no longer necessary or if processing is unlawful.</li>
          <li><strong>Restriction:</strong> request limited processing in certain cases.</li>
          <li><strong>Portability:</strong> receive data in a structured, commonly used, machine-readable format.</li>
          <li><strong>Object:</strong> object to processing based on legitimate interests.</li>
          <li><strong>Withdraw consent:</strong> for marketing communications at any time.</li>
          <li>
            <strong>Complaint:</strong> you can lodge a complaint with your local supervisory authority in the
            EEA or with the Swiss FDPIC.
          </li>
        </ul>
        <p>
          <strong>Note for U.S. Residents:</strong> Speqqle complies with applicable state privacy laws, including
          California’s CCPA/CPRA. U.S. users have equivalent rights to access, correction, deletion, and
          non-discrimination, which can be exercised via <a href="mailto:contact@speqqle.com">contact@speqqle.com</a>.
          We do not sell your personal information.
        </p>
      </div>

      <div className="section">
        <h2>10. Security</h2>
        <p>
          We implement appropriate technical and organizational measures to protect personal data, including
          encryption in transit, secure credential storage, role-based access control, least-privilege access,
          monitoring, and backup/restore procedures. No system is 100% secure, but we continuously improve our
          safeguards against unauthorized access, disclosure, alteration, or destruction.
        </p>
      </div>

      <div className="section">
        <h2>11. Children’s Privacy</h2>
        <p>
          The platform is intended for users aged 18 and older. We do not knowingly collect personal data from
          children. If you believe a child has provided personal data, please contact us for deletion.
        </p>
      </div>

      <div className="section">
        <h2>12. Changes to this Privacy Statement</h2>
        <p>
          We may update this Privacy Statement from time to time. If we make material changes, we will notify
          you by email or via in-platform notice. Continued use of the platform after the effective date
          constitutes your acknowledgment of the updated policy.
        </p>
      </div>

      <div className="section">
        <h2>13. Contact Us</h2>
        <p>
          If you have questions or would like to exercise your rights, please contact:
          <br />
          <a href="mailto:contact@speqqle.com">contact@speqqle.com</a>
        </p>
      </div>
    </div>
  );
}

export default PrivacyPage;
